import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';

const OptimizeModal = ({ 
  open, 
  onClose, 
  onComplete, // Add completion callback
  shipmentInfo,
  onOptimize,
  calculateRemainingQuantities 
}) => {
  const [selectedItems, setSelectedItems] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  // Get remaining quantities when modal opens
  const remainingItems = useMemo(() => {
    if (!open) return [];
    return calculateRemainingQuantities();
  }, [open, calculateRemainingQuantities]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedItems({});
      setIsProcessing(false);
      setCurrentStep('');
      setProgress(0);
      setResult(null);
      
      // Auto-select all items with remaining quantities
      const initialSelected = {};
      remainingItems.forEach(item => {
        initialSelected[item.item.merchant_sku] = true;
      });
      setSelectedItems(initialSelected);
    }
  }, [open, calculateRemainingQuantities]);

  const handleOptimize = async () => {
    setIsProcessing(true);
    setCurrentStep('Starting optimization...');
    setProgress(0);

    try {
      const itemsToOptimize = remainingItems.filter(item => selectedItems[item.item.merchant_sku]);
      
      if (itemsToOptimize.length === 0) {
        throw new Error('No items selected');
      }

      const updateProgress = (step, progressValue) => {
        setCurrentStep(step);
        setProgress(progressValue);
      };

      // Get the optimization result from the parent component
      const optimizationResult = await onOptimize(itemsToOptimize, updateProgress);
      
      // Set the result state - this triggers the result view
      setResult(optimizationResult);

    } catch (error) {
      console.error('Optimization failed:', error);
      setCurrentStep(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle close with completion callback for results screen
  const handleClose = () => {
    if (result && onComplete) {
      // If we're on the results screen, call completion callback
      onComplete();
    }
    onClose();
  };

  // Helper function to get the correct image URL with fallbacks
  const getItemImageUrl = (item) => {
    // Try to get image from images array first
    if (item.item.images && item.item.images.length > 0) {
      return item.item.images[0].large_url;
    }
    
    // Fallback to direct image URLs
    return (
      item.item.image_url ||
      item.item.main_image_url ||
      'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'
    );
  };

  const handleSelectAll = (checked) => {
    const newSelected = {};
    remainingItems.forEach(item => {
      newSelected[item.item.merchant_sku] = checked;
    });
    setSelectedItems(newSelected);
  };

  const allSelected = remainingItems.length > 0 && 
    remainingItems.every(item => selectedItems[item.item.merchant_sku]);
  const someSelected = remainingItems.some(item => selectedItems[item.item.merchant_sku]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {!result ? "Optimize Remaining Quantities" : "Optimization Complete"}
      </DialogTitle>
      <DialogContent>
        {isProcessing ? (
          <div className="text-center p-4">
            <CircularProgress />
            <Typography variant="h6" className="mt-2">
              {progress}% Complete
            </Typography>
            <Typography variant="body1" className="mt-1">
              {currentStep}
            </Typography>
            <LinearProgress variant="determinate" value={progress} className="mt-4" />
          </div>
        ) : result ? (
          <div className="space-y-4">
            {/* Summary Section */}
            <Card variant="outlined" className="mb-4">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Remainder Optimization Summary
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">SKUs Moved</Typography>
                    <Typography variant="h4">{result.summary.totalSKUs}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Units Moved</Typography>
                    <Typography variant="h4">{result.summary.totalUnits}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="h6" gutterBottom>
              Items Moved to New Outbound
            </Typography>

            {/* Items moved */}
            <div className="space-y-2">
              {result.selectedItems.map((item, index) => (
                <Card key={index} variant="outlined">
                  <CardContent className="flex items-center space-x-4 py-2">
                    <img
                      src={getItemImageUrl(item)}
                      alt={item.item.name || item.item.title}
                      className="w-12 h-12 object-contain"
                    />
                    <div className="flex-grow">
                      <Typography variant="subtitle2">
                        {item.item.name || item.item.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        SKU: {item.item.merchant_sku}
                      </Typography>
                      <div className="flex space-x-4 text-sm">
                        <span>Remaining: {item.remainingQuantity}</span>
                        {item.expiry_date && (
                          <span>Expires: {new Date(item.expiry_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Divider className="my-4" />
            
            <Typography variant="body2" color="textSecondary">
              The new outbound "Optimized Remainder [{shipmentInfo?.name}]" has been created and opened in a new tab.
              The original outbound quantities have been updated to remove the transferred amounts.
              You can now create plans and continue processing these remaining quantities separately.
            </Typography>
          </div>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Select items with remaining quantities to move to a new optimized outbound:
            </Typography>
            
            {remainingItems.length === 0 ? (
              <Typography color="success.main" variant="h6" className="text-center py-8">
                🎉 All items are fully boxed! No remaining quantities to optimize.
              </Typography>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    }
                    label={`Select All (${remainingItems.length} items with remaining quantities)`}
                  />
                </Box>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {remainingItems.map(item => (
                    <Box key={item.item.merchant_sku} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedItems[item.item.merchant_sku] || false}
                            onChange={(e) => setSelectedItems(prev => ({
                              ...prev,
                              [item.item.merchant_sku]: e.target.checked
                            }))}
                          />
                        }
                        label={
                          <Box className="flex items-center space-x-3 p-2 w-full">
                            <img
                              src={getItemImageUrl(item)}
                              alt={item.item.name || item.item.title}
                              className="w-12 h-12 object-contain"
                            />
                            <div className="flex-grow">
                              <Typography variant="subtitle2">
                                {item.item.name || item.item.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                SKU: {item.item.merchant_sku}
                              </Typography>
                              <div className="flex space-x-4 text-sm mt-1">
                                <span>Total: {item.totalQuantity}</span>
                                <span>Boxed: {item.quantityInBoxes}</span>
                                <span className="font-medium text-orange-600">
                                  Remaining: {item.remainingQuantity}
                                </span>
                                {item.expiry_date && (
                                  <span>Expires: {new Date(item.expiry_date).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                          </Box>
                        }
                        sx={{ margin: 0, display: 'flex', alignItems: 'flex-start' }}
                      />
                    </Box>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && !isProcessing && remainingItems.length > 0 && (
          <Button 
            onClick={handleOptimize} 
            variant="contained" 
            color="primary"
            disabled={!someSelected}
          >
            Create Remainder Outbound ({Object.values(selectedItems).filter(Boolean).length} items)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OptimizeModal; 